import { NumberFormatter } from "@mantine/core";
import { useDisclosure, useHotkeys, useMediaQuery } from "@mantine/hooks";
import * as React from "react";
import type { WorkBook } from "xlsx";
import Breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { getMasterOrgs } from "./api/saleOutwardApi";
import type { MasterOrg } from "./api/saleOutwardApi";
import DevicePickerModal from "./components/DevicePickerModal";
import ExcelSheetModal, { exportSerialsToExcel, pickExcelWorkbook } from "./components/ExcelSheetModal";
import ExchangeSerialPickerModal from "./components/ExchangeSerialPickerModal";
import ItemSearchMenu from "./components/ItemSearchMenu";
import OutwardHeading from "./components/OutwardHeading";
import OrderLinesTable from "./components/OrderLinesTable";
import SaleOrderInfoPanel from "./components/SaleOrderInfoPanel";
import SaleOutwardToolbar from "./components/SaleOutwardToolbar";
import ScanBar from "./components/ScanBar";
import ScannedSerialGrid from "./components/ScannedSerialGrid";
import { SALE_KIND_CONFIG } from "./config/saleKinds";
import type { SaleSourceType } from "./config/saleKinds";
import { TOP_BAR_HEIGHT } from "./config/theme";
import { useSaleOutwardData } from "./hooks/useSaleOutwardData";
import { useSaleOutwardSubmit } from "./hooks/useSaleOutwardSubmit";
import { useSalePrint } from "./hooks/useSalePrint";
import { useSerialScanner } from "./hooks/useSerialScanner";
import type { OutwardRoute } from "./routing/resolveOutwardRoute";
import { createInitialState, saleOutwardReducer } from "./state/saleOutwardReducer";
import { buildOrderLineView, totalScannedQuantity } from "./state/selectors";
import type { SaleOutwardError, ScannedSerialRow, SubmitStatus } from "./types";
import { scannedSerialSetOf } from "./utils/serial";
import { validateSaleOutward } from "./validation/validateSaleOutward";

interface SaleOutwardPageProps {
  route: OutwardRoute;
  sourceType: SaleSourceType;
}


/** Lỗi không tính là vướng mắc trên nút Hoàn thành (chế độ xem, đã khoá, chưa quét gì) */
const NON_ISSUE_CODES = new Set(["READ_ONLY", "LOCKED", "NO_ROWS"]);

/** Sau khoảng này bỏ yêu cầu cuộn tới dòng, để bấm lại cùng dòng vẫn cuộn được */
const FOCUS_RESET_MS = 2000;

const ORDER_SECTION_HEIGHT = 300;
/**
 * ScannedSerialGrid dựng khung cao `height - 100`, OrderLinesTable cao `height * 0.67` (quy ước của trang cũ).
 * Trang đo vùng trống rồi quy đổi ngược để hai bảng lấp vừa khít khối.
 */
const GRID_HEIGHT_OFFSET = 100;
const ORDER_TABLE_HEIGHT_RATIO = 0.67;
/** Màn hẹp xếp các khối theo cột, khối "Bên xuất" có chiều cao cố định */
const SMALL_SCREEN_SCAN_SECTION_HEIGHT = 520;
/** Cửa sổ quá thấp thì trang cuộn thay vì ép lưới serial quá bé */
const MIN_PAGE_HEIGHT = 720;

/**
 * Chiều cao vùng nội dung dưới header của AppShell. Trang kéo tràn cả padding trên và đáy (BLEED) nên phủ kín
 * từ ngay dưới header tới đáy màn hình. Breadcrumb đặt zoom 93% cho body ở các trang xuất kho; 100dvh không co
 * theo zoom nên chia lại cho đủ cao.
 */
const viewportHeight = (zoom: number) => `calc(100dvh / ${zoom} - var(--app-shell-header-offset, 0rem))`;
const BLEED = "calc(var(--app-shell-padding, 0rem) * -1)";

const CARD: React.CSSProperties = {
  boxSizing: "border-box",
  background: "#FFFFFF",
  border: "1px solid #DDE0DA",
  borderRadius: 12,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const CARD_BAR: React.CSSProperties = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  borderBottom: "1px solid #ECEEE9",
};

const CARD_TITLE: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "20px",
  color: "#1B1F24",
  whiteSpace: "nowrap",
};

/** Vùng co giãn theo khối cha; nội dung đặt tuyệt đối nên không đẩy ngược kích thước khối */
const FILL_AREA: React.CSSProperties = { flex: "1 1 0", minHeight: 0, position: "relative" };
const FILL_INNER: React.CSSProperties = { position: "absolute", inset: 0, display: "flex", flexDirection: "column" };

const readDepartmentId = (): number | null => {
  try {
    return JSON.parse(localStorage.getItem("userLogin") || "null")?.depId ?? null;
  } catch {
    return null;
  }
};

/** Chiều cao thực của một vùng, cập nhật khi đổi kích thước (lưới AG Grid / bảng cần số cụ thể) */
const useElementHeight = <T extends HTMLElement>() => {
  const ref = React.useRef<T>(null);
  const [height, setHeight] = React.useState(0);

  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const update = () => setHeight(element.clientHeight);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, height] as const;
};

const readBodyZoom = () => {
  const zoom = parseFloat(getComputedStyle(document.body).getPropertyValue("zoom"));
  return zoom > 0 ? zoom : 1;
};

/** Zoom hiện tại của body; Breadcrumb đổi zoom theo đường dẫn nên theo dõi thuộc tính style */
const useBodyZoom = () => {
  const [zoom, setZoom] = React.useState(readBodyZoom);
  React.useEffect(() => {
    const update = () => setZoom(readBodyZoom());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    return () => observer.disconnect();
  }, []);
  return zoom;
};

/** Yêu cầu cuộn tới một dòng; tự bỏ sau FOCUS_RESET_MS */
const useTransientFocus = () => {
  const [value, setValue] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (value === null) return undefined;
    const timer = window.setTimeout(() => setValue(null), FOCUS_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [value]);
  return [value, setValue] as const;
};

/**
 * Trang phiếu xuất kho bán hàng (XUAT_BAN) và xuất đổi trả (XUAT_DOI_TRA).
 * Tách riêng khỏi WarehouseOutWardActionV2 để thêm bảo hành mở rộng mà không ảnh hưởng
 * các loại phiếu xuất khác. Mở qua OutwardActionDispatcher trên 3 URL xuất kho cũ.
 */
const SaleOutwardPage = ({ route, sourceType }: SaleOutwardPageProps) => {
  const kind = SALE_KIND_CONFIG[sourceType];
  const isSmallScreen = useMediaQuery("(max-width: 1250px)") ?? false;
  const pageHeight = viewportHeight(useBodyZoom());
  const [state, dispatch] = React.useReducer(saleOutwardReducer, undefined, () => {
    const initial = createInitialState(sourceType, route.sourceCode || route.transactionCode || "");
    initial.header.departmentId = readDepartmentId();
    return initial;
  });
  const { header, order, orderLines, rows, transactionId } = state;

  const readOnly = route.action === "DETAIL" || route.isLocked;
  const locked = header.status === "LOCK";
  const editDisabled = readOnly || locked;

  const [masterOrgs, setMasterOrgs] = React.useState<MasterOrg[]>([]);
  React.useEffect(() => {
    getMasterOrgs().then(setMasterOrgs);
  }, []);

  const { loadError } = useSaleOutwardData(route, sourceType, dispatch);
  const lines = React.useMemo(() => buildOrderLineView(orderLines, rows), [orderLines, rows]);
  const scannedSerials = scannedSerialSetOf(rows);
  const totalQuantity = React.useMemo(() => totalScannedQuantity(rows), [rows]);
  // Lưới đọc rows mới nhất qua ref khi xuất Excel, để gridHandlers không đổi mỗi lần quét
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;

  // BHMR: thẻ đang chọn thiết bị, dòng serial / dòng đơn cần cuộn tới
  const [cardSerialForPicker, setCardSerialForPicker] = React.useState<string | null>(null);
  // Xuất đổi trả: serial xuất đang chọn "Serial nhập"
  const [serialForExchangePicker, setSerialForExchangePicker] = React.useState<string | null>(null);
  const [focusSerial, setFocusSerial] = useTransientFocus();
  const [focusItemCode, setFocusItemCode] = useTransientFocus();

  // "Tới dòng" / "Chọn thiết bị" trong hộp lỗi chặn
  const goToIssue = React.useCallback(
    (error: SaleOutwardError) => {
      if (error.code === "CARD_NOT_LINKED" && error.serial) {
        setCardSerialForPicker(error.serial);
        return;
      }
      if (error.itemCode) {
        setFocusItemCode(error.itemCode);
        return;
      }
      if (error.serial) setFocusSerial(error.serial);
    },
    [setFocusItemCode, setFocusSerial],
  );

  const { scan, addMany, scanning, lastAdded } = useSerialScanner(state, dispatch);
  const { submit, submitting, lastLockedId } = useSaleOutwardSubmit({
    state,
    dispatch,
    lines,
    kind,
    readOnly,
    onGoToIssue: goToIssue,
  });
  const print = useSalePrint({ sourceType, header, branchId: order.fromBranchId, rows, orderLines: lines });

  const [selectedRows, setSelectedRows] = React.useState<ScannedSerialRow[]>([]);
  const [keyword, setKeyword] = React.useState("");
  // Bảng kết quả tìm serial: bấm nút trên header thì đóng, chọn mã ở bảng Thông tin đơn hàng thì mở lại
  const [searchCloseSignal, setSearchCloseSignal] = React.useState(0);
  const [searchOpenSignal, setSearchOpenSignal] = React.useState(0);
  const pickOrderItem = React.useCallback((itemCode: string) => {
    setKeyword(itemCode);
    setSearchOpenSignal((n) => n + 1);
  }, []);
  const [workbook, setWorkbook] = React.useState<WorkBook | null>(null);
  const [collapsed, { toggle: toggleCollapse }] = useDisclosure(false);

  const [gridAreaRef, gridHeight] = useElementHeight<HTMLDivElement>();
  const [orderAreaRef, orderTableHeight] = useElementHeight<HTMLDivElement>();

  const onSubmit = React.useCallback((status: SubmitStatus) => submit(status), [submit]);

  useHotkeys([
    // Phím tắt đi qua cùng hàm submit với nút; ở chế độ xem / đã khoá thì bỏ qua như nút bị disable
    ["ctrl+S", () => !editDisabled && submit("OPEN")],
    ["ctrl+Q", () => !editDisabled && submit("LOCK")],
    ["ctrl+shift+Z", () => toggleCollapse()],
  ]);

  const removeRows = React.useCallback(
    (serials: string[]) => {
      dispatch({ type: "REMOVE_ROWS", serials });
      const removed = new Set(serials);
      setSelectedRows((prev) => prev.filter((row) => !removed.has(String(row.serial))));
    },
    [],
  );

  const gridHandlers = React.useMemo(
    () => ({
      onRemove: removeRows,
      onQuantityChange: (serial: string, quantity: number) =>
        dispatch({ type: "SET_ROW_QUANTITY", serial, quantity }),
      onNoteChange: (serial: string, note: string | null) => dispatch({ type: "SET_ROW_NOTE", serial, note }),
      onPrintAssembly: print.printAssemblyParts,
      onImportExcel: () => pickExcelWorkbook(setWorkbook),
      onExportExcel: () => exportSerialsToExcel(rowsRef.current),
      onSelectionChange: setSelectedRows,
    }),
    [removeRows, print.printAssemblyParts],
  );

  const openDevicePicker = React.useCallback(
    (cardSerial: string) => {
      if (!editDisabled) setCardSerialForPicker(cardSerial);
    },
    [editDisabled],
  );
  const closeDevicePicker = React.useCallback(() => setCardSerialForPicker(null), []);
  const linkDevice = React.useCallback((cardSerial: string, deviceSerial: string) => {
    dispatch({ type: "LINK_DEVICE", cardSerial, deviceSerial });
    setCardSerialForPicker(null);
  }, []);
  const unlinkDevice = React.useCallback((cardSerial: string) => {
    dispatch({ type: "UNLINK_DEVICE", cardSerial });
    setCardSerialForPicker(null);
  }, []);

  // Xuất đổi trả: chọn serial khách trả cho một serial xuất
  const openExchangePicker = React.useCallback(
    (serial: string) => {
      if (!editDisabled) setSerialForExchangePicker(serial);
    },
    [editDisabled],
  );
  const closeExchangePicker = React.useCallback(() => setSerialForExchangePicker(null), []);
  const setExchangeSerials = React.useCallback((serial: string, oldSerials: string[]) => {
    dispatch({ type: "SET_EXCHANGE_SERIALS", serial, oldSerials });
    setSerialForExchangePicker(null);
  }, []);

  const handleChangeDescription = React.useCallback(
    (value: string) => dispatch({ type: "SET_HEADER_FIELD", key: "description", value }),
    [],
  );

  // Số vướng mắc nếu hoàn thành lúc này, cùng cách tính với lúc bấm (kể cả thiếu kho xuất).
  // Chỉ hiện khi còn sửa được và đã có serial (chưa có serial thì nút Hoàn thành đang tắt)
  const issueCount = React.useMemo(() => {
    if (editDisabled || rows.length === 0) return 0;
    const issues = validateSaleOutward({ status: "LOCK", header, rows, lines, kind, readOnly }).filter(
      (error) => !NON_ISSUE_CODES.has(error.code),
    ).length;
    return issues + (order.fromInvId ? 0 : 1);
  }, [editDisabled, header, rows, lines, kind, readOnly, order.fromInvId]);

  // Dòng trạng thái bị cắt khi thanh hẹp; tooltip giữ đủ nội dung kèm tên hàng
  const lastAddedTitle = lastAdded
    ? [`Đã thêm ${lastAdded.serial}`, lastAdded.remainText, lastAdded.itemName].filter(Boolean).join(" · ")
    : undefined;

  // Kết quả Hoàn thành hiện cố định ở dòng trạng thái của thanh Bên xuất. Toast không đủ: mọi request qua HttpHelper
  // gọi notifications.clean(), mà layout tải lại chuông thông báo ngay khi có tin socket sau khoá phiếu nên toast bị xoá
  const lockedTime = header.finishDate ? new Date(header.finishDate) : null;
  const lockNotice =
    lastLockedId != null
      ? [
          `Đã hoàn thành phiếu ${header.transactionCode ?? ""}${
            lockedTime && !Number.isNaN(lockedTime.getTime())
              ? ` lúc ${lockedTime.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}`
              : ""
          }.`,
          kind.createShippingOrders || kind.sendZns
            ? "Đang tạo đơn vận chuyển và gửi ZNS (nếu có), kết quả xem ở chuông thông báo."
            : "",
        ]
          .filter(Boolean)
          .join(" ")
      : null;

  // Phiếu vừa khoá trên trang tạo: in theo id backend trả về. Trang sửa / xem: id của phiếu.
  const printableId = locked ? lastLockedId ?? (transactionId || null) : null;

  // Màn rộng: các khối ghép sát nhau (không khoảng cách, viền chồng 1px), chỉ bo các góc ngoài.
  // Màn hẹp các khối xếp dọc nên giữ khoảng cách như cũ
  const joined = !isSmallScreen;
  const joinedLeft = joined && !collapsed;

  return (
    <div
      style={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 8,
        // Kéo nền tràn qua padding bốn cạnh của AppShell để phủ kín vùng nội dung, sát ngay dưới header
        margin: BLEED,
        height: isSmallScreen ? undefined : pageHeight,
        minHeight: isSmallScreen ? pageHeight : MIN_PAGE_HEIGHT,
        background: "#FFFFFF",
        color: "#1B1F24",
        fontSize: 13,
      }}
    >
      <ExcelSheetModal workbook={workbook} onClose={() => setWorkbook(null)} onConfirm={addMany} />
      <DevicePickerModal
        opened={cardSerialForPicker !== null}
        cardSerial={cardSerialForPicker}
        rows={rows}
        onClose={closeDevicePicker}
        onConfirm={linkDevice}
        onUnlink={unlinkDevice}
      />
      {kind.exchangeSerialColumn && (
        <ExchangeSerialPickerModal
          opened={serialForExchangePicker !== null}
          serial={serialForExchangePicker}
          rows={rows}
          returnSerials={order.returnSerials ?? []}
          onClose={closeExchangePicker}
          onConfirm={setExchangeSerials}
        />
      )}
      {/* Breadcrumb và thanh nút chung một hàng; thanh nút dạt phải */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, flexGrow: 1, minWidth: 0 }}>
          <Breadcrumb />
          {/* Loại phiếu nằm ở đầu khối Thông tin chung; khối bị ẩn thì hiện ở đây */}
          {collapsed && (
            <OutwardHeading kindLabel={kind.label} status={header.status} />
          )}
          {loadError && (
            <span role="alert" style={{ color: "#B42318", fontSize: 12, fontWeight: 600 }}>
              Không tải được dữ liệu phiếu.
            </span>
          )}
        </div>
        {/* Bắt sự kiện ở pha capture để đóng bảng tìm serial trước khi nút xử lý; display: contents giữ nguyên bố cục */}
        <div style={{ display: "contents" }} onMouseDownCapture={() => setSearchCloseSignal((n) => n + 1)}>
        <SaleOutwardToolbar
          kind={kind}
          isSmallScreen={isSmallScreen}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          readOnly={readOnly}
          locked={locked}
          hasRows={rows.length > 0}
          selectedCount={selectedRows.length}
          onDeleteSelected={() => removeRows(selectedRows.map((row) => String(row.serial)))}
          submitting={submitting}
          onSubmit={onSubmit}
          issueCount={issueCount}
          printableId={printableId}
          onPrint={(id) => print.printOutward(id, kind.printWithOptions)}
          showHandover={kind.handoverPrint && route.action === "DETAIL" && header.tenCty != null}
          onDownloadHandover={print.downloadHandoverTemplate}
          onPrintHandover={print.openHandoverOptions}
          ecommerceButton={kind.ecommercePrint ? print.ecommerceButton : null}
          onPrintPickingList={print.printPickingList}
        />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: joined ? 0 : 12,
          flex: isSmallScreen ? "none" : "1 1 0",
          minHeight: 0,
        }}
      >
        {!collapsed && (
          <SaleOrderInfoPanel
            kindLabel={kind.label}
            header={header}
            order={order}
            masterOrgs={masterOrgs}
            editDisabled={editDisabled}
            onChangeDescription={handleChangeDescription}
            joinedRight={joined}
          />
        )}

        <div
          style={{
            flex: isSmallScreen ? "none" : "1 1 0",
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            gap: joined ? 0 : 12,
            marginLeft: joinedLeft ? -1 : undefined,
          }}
        >
          <section
            aria-label="Bên xuất"
            style={{
              ...CARD,
              flex: isSmallScreen ? "none" : "1 1 0",
              height: isSmallScreen ? SMALL_SCREEN_SCAN_SECTION_HEIGHT : undefined,
              minHeight: 0,
              ...(joined && {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                ...(joinedLeft && { borderTopLeftRadius: 0 }),
              }),
            }}
          >
            <div
              style={{
                ...CARD_BAR,
                flexWrap: "wrap",
                rowGap: 8,
                // Cao bằng thanh Loại phiếu bên trái để tiêu đề lưới thẳng hàng tiêu đề Bên xuất / Bên nhận
                boxSizing: "border-box",
                minHeight: TOP_BAR_HEIGHT,
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              <h2 style={{ ...CARD_TITLE, marginRight: 6 }}>Bên xuất</h2>
              <ScanBar disabled={editDisabled} scanning={scanning} onScan={scan} />
              <ItemSearchMenu
                keyword={keyword}
                onKeywordChange={setKeyword}
                sourceCode={header.sourceCode ?? ""}
                sourceType={sourceType}
                disabled={editDisabled}
                scannedSerials={scannedSerials}
                onPick={(serial, positionId) => scan(serial, positionId)}
                closeSignal={searchCloseSignal}
                openSignal={searchOpenSignal}
              />
              <span
                role="status"
                title={lockNotice ?? lastAddedTitle}
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#14532F",
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {(lockNotice || lastAdded) && (
                  <>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      style={{ flexShrink: 0 }}
                    >
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                    {lockNotice ? (
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600 }}>{lockNotice}</span>
                    ) : (
                      lastAdded && (
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                          Đã thêm{" "}
                          <b style={{ fontWeight: 600 }}>{lastAdded.serial}</b>
                          {lastAdded.remainText && ` · ${lastAdded.remainText}`}
                        </span>
                      )
                    )}
                  </>
                )}
              </span>
              {/* Gộp nhãn và số để khi thanh xuống dòng thì cả cụm nằm bên phải */}
              <span style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto", whiteSpace: "nowrap" }}>
                <span style={{ fontWeight: 700 }}>Số lượng:</span>
                <span style={{ color: "#B42318", fontWeight: 700, fontSize: 15 }}>
                  <NumberFormatter value={totalQuantity} thousandSeparator />
                </span>
              </span>
            </div>
            <div ref={gridAreaRef} style={FILL_AREA}>
              <div style={FILL_INNER}>
                <ScannedSerialGrid
                  rows={rows}
                  orderLines={orderLines}
                  readOnly={readOnly}
                  locked={locked}
                  height={gridHeight + GRID_HEIGHT_OFFSET}
                  action={route.action}
                  {...gridHandlers}
                  onPickDevice={openDevicePicker}
                  showExchangeColumn={kind.exchangeSerialColumn}
                  onPickExchangeSerial={openExchangePicker}
                  focusSerial={focusSerial}
                />
              </div>
            </div>
          </section>

          <section
            aria-label={kind.orderSectionTitle}
            style={{
              ...CARD,
              height: ORDER_SECTION_HEIGHT,
              flexShrink: 0,
              ...(joined && {
                marginTop: -1,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                ...(joinedLeft && { borderBottomLeftRadius: 0 }),
              }),
            }}
          >
            <div style={CARD_BAR}>
              <h2 style={CARD_TITLE}>{kind.orderSectionTitle}</h2>
            </div>
            <div ref={orderAreaRef} style={FILL_AREA}>
              <div style={FILL_INNER}>
                <OrderLinesTable
                  lines={lines}
                  height={orderTableHeight / ORDER_TABLE_HEIGHT_RATIO}
                  readOnly={editDisabled}
                  onPickItem={pickOrderItem}
                  focusItemCode={focusItemCode}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
      {print.printArea}
    </div>
  );
};

export default SaleOutwardPage;
